'use client'

import { modal } from '@reown/appkit/react'

type AppKitInstance = NonNullable<typeof modal>
type OpenArgs = Parameters<AppKitInstance['open']>
type SubscribeStateArgs = Parameters<AppKitInstance['subscribeState']>
type SubscribeEventsArgs = Parameters<AppKitInstance['subscribeEvents']>

const noop = () => {}

// SSR-safe AppKit modal handle (stable singleton); each call no-ops / returns false before `createAppKit`.
const appKit = {
  open: async (...args: OpenArgs) => modal?.open(...args),
  close: async () => modal?.close(),
  disconnect: async () => modal?.disconnect?.(),
  subscribeState: (...args: SubscribeStateArgs) => modal?.subscribeState?.(...args) ?? noop,
  subscribeEvents: (...args: SubscribeEventsArgs) => modal?.subscribeEvents?.(...args) ?? noop,
  getIsConnectedState: () => modal?.getIsConnectedState?.() ?? false
}

/**
 * The AppKit modal control surface returned by {@link useAppKitSSR}
 * (open/close/disconnect/subscribeState/subscribeEvents/getIsConnectedState). Exported so
 * consumers can name the modal surface without re-deriving the `ReturnType`.
 *
 * Note: `subscribeState` and `subscribeEvents` calls made before `createAppKit` runs are **dropped,
 * not deferred** — it returns `noop` and the subscription is lost, and because
 * this handle is a referentially-stable singleton a `[modal]`-keyed effect
 * won't retry. This is safe today only because consumers mount below
 * `Web3Provider` (which calls `createAppKit` in its render body); subscribe
 * from an effect in that subtree, not at module scope.
 */
export type AppKitSSRHandle = typeof appKit

export function useAppKitSSR(): AppKitSSRHandle {
  return appKit
}
